import { useState } from 'react'
import {
  PatientFormData,
  PatientInformationFormProps,
  FormErrors,
} from './PatientInformationForm.types'
import dayjs from 'lib/dayjs'
import { verifyPatientInformation } from 'lib/utils/patientVerification'

export const usePatientInformationForm = ({
  onSubmit,
  invitationData,
}: Pick<PatientInformationFormProps, 'onSubmit' | 'invitationData'>) => {
  const [formData, setFormData] = useState<PatientFormData>({
    firstName: '',
    lastName: '',
    dateOfBirth: null,
    consentChecked: false,
  })

  const [errors, setErrors] = useState<FormErrors>({})

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target

    if (type === 'checkbox') {
      const checkbox = e.target as HTMLInputElement
      setFormData((prev) => ({
        ...prev,
        [name]: checkbox.checked,
      }))
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }))
    }

    // Clear error when field is edited
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

  const handleDateChange = (date: dayjs.Dayjs | null) => {
    setFormData((prev) => ({
      ...prev,
      dateOfBirth: date,
    }))

    // Clear date error
    if (errors.dateOfBirth) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors.dateOfBirth
        return newErrors
      })
    }
  }

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}
    const isProduction = process.env.REACT_APP_ENV === 'production'

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required'
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required'
    }

    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = 'Date of birth is required'
    }

    if (!formData.consentChecked) {
      newErrors.consentChecked = 'You must consent to receive your wallet card'
    }

    // Only verify patient information against invitation data in production
    if (
      isProduction &&
      invitationData &&
      formData.firstName.trim() &&
      formData.lastName.trim()
    ) {
      const verification = verifyPatientInformation(
        formData.firstName.trim(),
        formData.lastName.trim(),
        formData.dateOfBirth,
        invitationData
      )

      if (!verification.isValid) {
        Object.assign(newErrors, verification.errors)
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    onSubmit(formData)
  }

  const isFormComplete = () => {
    return (
      formData.firstName.trim() !== '' &&
      formData.lastName.trim() !== '' &&
      formData.dateOfBirth !== null &&
      formData.consentChecked
    )
  }

  return {
    formData,
    errors,
    handleChange,
    handleDateChange,
    handleSubmit,
    isFormComplete: isFormComplete(),
  }
}
